// src/App.js
import React, { useState, useEffect, lazy, Suspense } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Link,
  useNavigate,
  useLocation,
  useParams,
} from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Button,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Tooltip,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MenuIcon from '@mui/icons-material/Menu';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import Sidebar, { drawerWidths } from './components/Sidebar';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

import './App.css';

import Home from './routes/Home';
import EventPage from './components/EventPage';
import PrivateRoute from './components/PrivateRoute';
import SignUp from './components/SignUp';
import SignIn from './components/SignIn';
import EventDashboard from './components/EventDashboard';
import EventPreviewForm from './components/EventPreviewForm';


const EventCreationForm = lazy(() => import('./components/EventCreationForm'));
const Subpage = lazy(() => import('./components/Subpage'));


import logo from './assets/logo.svg';
import { app } from './firebase/firebaseConfig';

// ✅ Lazy-load NON-event / non-critical routes only
const ComparePage = lazy(() => import('./pages/ComparePage'));
const FAQ = lazy(() => import('./components/FAQ'));
const TermsAndConditions = lazy(() => import('./components/TermsAndConditions'));
const PrivatePolicy = lazy(() => import('./components/PrivatePolicy'));
const QRCodeTemplates = lazy(() => import('./components/QRCodeTemplates'));
const Docs = lazy(() => import('./components/Docs'));
const HelpSupport = lazy(() => import('./components/HelpSupport'));
const EmailActionHandler = lazy(() => import('./components/EmailActionHandler'));
const PostVerify = lazy(() => import('./pages/PostVerify'));
const AppleCallback = lazy(() => import('./pages/AppleCallback'));
const CompliancePage = lazy(() => import('./pages/CompliancePage'));
const StorybookFAQ = lazy(() => import('./components/StorybookFAQ'));
const About = lazy(() => import('./pages/About'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const ElevateEventReviews = lazy(() => import('./pages/ElevateEventReviews'));

function App() {
  return (
    <Router>
      <AppWrapper />
    </Router>
  );
}

/**
 * ✅ Redirects /edit-event/:eventId/step1 -> /edit-event/:eventId/step/1 (etc)
 * This prevents EventCreationForm from remounting with stepParam undefined (which resets to step 1).
 */
const EditStepAliasRedirect = ({ stepNumber }) => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!eventId) return;
    navigate(`/edit-event/${encodeURIComponent(eventId)}/step/${stepNumber}`, {
      replace: true,
      state: location.state,
    });
  }, [eventId, stepNumber, navigate, location.state]);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
      <CircularProgress />
    </Box>
  );
};

/**
 * ✅ Redirect any old QR edit routes (step 3) to EventPreviewForm.
 * This ensures clicking QR never enters edit flow.
 */
const EditQrRedirect = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!eventId) return;
    navigate(`/eventpreview/${encodeURIComponent(eventId)}`, {
      replace: true,
      state: location.state,
    });
  }, [eventId, navigate, location.state]);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
      <CircularProgress />
    </Box>
  );
};

/**
 * ✅ Handles /edit-event/:eventKey where eventKey might be:
 * - Firestore doc id  (events/{eventId})
 * - OR a slug/uniqueUrl (like "jack--ana-")
 *
 * It resolves to the real doc id, then navigates to /edit-event/{docId}/step/1
 */
const EditEventEntryResolver = () => {
  const { eventKey } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!eventKey) return;

      const db = getFirestore();

      // 1) First, try treating eventKey as a doc id
      try {
        const snap = await getDoc(doc(db, 'events', eventKey));
        if (!cancelled && snap.exists()) {
          navigate(`/edit-event/${encodeURIComponent(eventKey)}/step/1`, {
            replace: true,
            state: location.state,
          });
          return;
        }
      } catch (e) {
        // ignore and try uniqueUrl lookup
      }

      // 2) Otherwise, treat it as uniqueUrl slug
      try {
        const q = query(collection(db, 'events'), where('uniqueUrl', '==', eventKey));
        const qs = await getDocs(q);

        if (!cancelled && !qs.empty) {
          const docId = qs.docs[0].id;
          navigate(`/edit-event/${encodeURIComponent(docId)}/step/1`, {
            replace: true,
            state: location.state,
          });
          return;
        }
      } catch (e) {
        console.error('EditEventEntryResolver uniqueUrl lookup failed:', e);
      }

      // 3) If nothing found, go back safely
      if (!cancelled) {
        navigate('/eventdashboard', { replace: true });
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [eventKey, navigate, location.state]);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
      <CircularProgress />
    </Box>
  );
};

const withTimeout = (promise, ms, label = "operation") =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`${label} timed out`)), ms)
    ),
  ]);
const AppWrapper = () => {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuType, setMenuType] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [portalUrl, setPortalUrl] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state;

  // ✅ Use this for UI decisions so the app does NOT "switch" visually when /subpage is opened as an overlay
  const uiLocation = state?.backgroundLocation || location;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const sidebarRoutes = [
    '/create-event',
    '/edit-event',
    '/eventdashboard',
    '/eventpreview',
    '/qrcode-templates',
    '/docs',
  ];
  const isSidebarRoute = sidebarRoutes.some((route) => uiLocation.pathname.startsWith(route));

  const isPublicEvent = uiLocation.pathname.startsWith('/events/');

  useEffect(() => {
  const auth = getAuth();

  const unsubscribe = onAuthStateChanged(
    auth,
    async (user) => {
      setLoading(true);

      try {
        if (user) {
          setCurrentUser(user);

          // ✅ Do not let public event pages get stuck waiting on user subscription lookup.
          if (window.location.pathname.startsWith('/events/')) {
            setHasSubscription(false);
            return;
          }

          try {
            const db = getFirestore();
            const userDocRef = doc(db, 'users', user.uid);

            // ✅ Prevent brand-new Google sign-in from spinning forever.
            const userDoc = await withTimeout(
              getDoc(userDocRef),
              4000,
              'User subscription lookup'
            );

            setHasSubscription(
              userDoc.exists() && userDoc.data().subscriptionStatus === 'active'
            );
          } catch (err) {
            console.warn('User subscription lookup skipped:', err);
            setHasSubscription(false);
          }
        } else {
          setCurrentUser(null);
          setHasSubscription(false);
        }
      } catch (err) {
        console.error('Auth state handling failed:', err);
        setCurrentUser(user || null);
        setHasSubscription(false);
      } finally {
        // ✅ Most important line: never allow the app-level spinner to stay forever.
        setLoading(false);
      }
    },
    (error) => {
      console.error('Auth listener failed:', error);
      setCurrentUser(null);
      setHasSubscription(false);
      setLoading(false);
    }
  );

  return () => unsubscribe();
}, []);

  useEffect(() => {
    const handlePageShow = () => {
      setIsRedirecting(false);
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  const prefetchPortalUrl = async () => {
    try {
      const functions = getFunctions();
      const createPortalLink = httpsCallable(
        functions,
        'ext-firestore-stripe-payments-createPortalLink'
      );
      const { data } = await createPortalLink({ returnUrl: window.location.origin });
      setPortalUrl(data.url);
    } catch (error) {
      console.error('Error prefetching portal URL:', error);
    }
  };

  useEffect(() => {
    if (currentUser && !isPublicEvent) {
      prefetchPortalUrl();
    }
  }, [currentUser, isPublicEvent]);

  const sendToCustomerPortal = async () => {
    try {
      setIsRedirecting(true);
      if (portalUrl) {
        window.location.assign(portalUrl);
      } else {
        const functions = getFunctions();
        const createPortalLink = httpsCallable(
          functions,
          'ext-firestore-stripe-payments-createPortalLink'
        );
        const { data } = await createPortalLink({ returnUrl: window.location.origin });
        window.location.assign(data.url);
      }
    } catch (error) {
      console.error('Error redirecting to Stripe customer portal:', error);
      setIsRedirecting(false);
    }
  };

  const handleMenuClick = (event, type) => {
    setAnchorEl(event.currentTarget);
    setMenuType(type);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuType(null);
  };

  const handleLogout = () => {
    const auth = getAuth();
    auth
      .signOut()
      .then(() => navigate('/', { replace: true }))
      .catch((error) => console.error('Error signing out:', error));
    handleMenuClose();
  };

  const sendToCustomerPortalOnHover = () => {
    if (!portalUrl) {
      prefetchPortalUrl();
    }
  };

  const hasPermanentDrawer =
    currentUser &&
    !isMobile &&
    !isPublicEvent &&
    isSidebarRoute;

 if (loading && !isPublicEvent) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
      }}
    >
      <CircularProgress size={50} />
    </Box>
  );
}

  return (
    <div>
      <Box sx={{ display: 'flex', overflow: 'hidden' }}>
        {isRedirecting && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999,
            }}
          >
            <CircularProgress size={60} sx={{ color: '#fff' }} />
          </Box>
        )}

        {!isPublicEvent &&
          currentUser &&
          (isMobile ? (
            <Sidebar
              mobileOpen={mobileOpen}
              setMobileOpen={setMobileOpen}
              isAuthenticated={!!currentUser}
            />
          ) : (
            <Sidebar isAuthenticated={!!currentUser} />
          ))}

        <Box
          sx={{
            flex: '1 1 auto',
            minWidth: 0,
            overflowX: 'hidden',
            ml: hasPermanentDrawer
              ? {
                  md: `${drawerWidths.md}px`,
                  lg: `${drawerWidths.lg}px`,
                }
              : 0,
          }}
        >
          {!isPublicEvent && (
            <AppBar
              position="fixed"
              sx={{
                top: 0,
                left: 0,
                right: 0,
                background:
                  uiLocation.pathname === '/signup' ||
                  uiLocation.pathname === '/signin' ||
                  uiLocation.pathname === '/subpage'
                    ? '#000'
                    : currentUser
                    ? '#000'
                    : 'white',
                color:
                  uiLocation.pathname === '/signup' ||
                  uiLocation.pathname === '/signin' ||
                  uiLocation.pathname === '/subpage' ||
                  !!currentUser
                    ? '#fff'
                    : '#000',
                boxShadow: 'none',
                zIndex: (theme) => theme.zIndex.drawer + 1,
              }}
            >
              <Toolbar>
                {isMobile ? (
                  isSidebarRoute ? (
                    <IconButton
                      edge="start"
                      color="inherit"
                      onClick={() => setMobileOpen(!mobileOpen)}
                    >
                      <MenuIcon sx={{ fontSize: '32px' }} />
                    </IconButton>
                  ) : (
                    <IconButton edge="start" color="inherit" component={Link} to="/">
                      <Box
                        component="img"
                        src={logo}
                        alt="Logo"
                        sx={{ height: { xs: '50px', md: '80px' }, marginRight: '10px' }}
                      />
                    </IconButton>
                  )
                ) : (
                  <IconButton edge="start" color="inherit" component={Link} to="/">
                    <Box
                      component="img"
                      src={logo}
                      alt="Logo"
                      sx={{ height: { xs: '50px', md: '80px' }, marginRight: '10px' }}
                    />
                  </IconButton>
                )}

                <Box sx={{ flexGrow: 1 }} />

                {currentUser ? (
                  <>
                    <Tooltip title="Give Feedback">
                      <IconButton
                        color="inherit"
                        onClick={(e) => handleMenuClick(e, 'help')}
                        sx={{ fontSize: '32px', '& svg': { fontSize: '32px' } }}
                      >
                        <HelpOutlineIcon />
                      </IconButton>
                    </Tooltip>

                    <IconButton
                      color="inherit"
                      onClick={(e) => handleMenuClick(e, 'account')}
                      sx={{ fontSize: '32px', '& svg': { fontSize: '32px' } }}
                    >
                      <AccountCircleIcon />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <Button
                      component={Link}
                      to="/signup"
                      color="inherit"
                      sx={{ fontSize: '18px', display: { xs: 'block', sm: 'block' } }}
                    >
                      Sign Up
                    </Button>
                    <Button
                      component={Link}
                      to="/signin"
                      color="inherit"
                      sx={{ fontSize: '18px', display: { xs: 'block', sm: 'block' } }}
                    >
                      Sign In
                    </Button>
                  </>
                )}
              </Toolbar>
            </AppBar>
          )}

          {menuType === 'account' && (
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onMouseEnter={sendToCustomerPortalOnHover} onClick={sendToCustomerPortal}>
                Manage Account
              </MenuItem>
              <MenuItem onClick={handleLogout}>Log Out</MenuItem>
            </Menu>
          )}

          {menuType === 'help' && (
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem
                onClick={() => {
                  navigate('/help');
                  handleMenuClose();
                }}
              >
                Open Feedback Form
              </MenuItem>
            </Menu>
          )}

        <Box
  sx={{
    width: '100%',
    paddingTop: isPublicEvent ? 0 : { xs: '64px', md: '80px' },
  }}
>
            <Suspense
              fallback={
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                  <CircularProgress />
                </Box>
              }
            >
              <Routes location={state?.backgroundLocation || location}>
                <Route path="/" element={<Home />} />
                <Route path="/signup" element={<SignUp />} />
                <Route path="/signin" element={<SignIn />} />
                <Route path="/eventdashboard" element={<EventDashboard />} />

                {/* ✅ QR Preview route (dashboard QR icon goes here) */}
                <Route path="/eventpreview/:eventId" element={<PrivateRoute element={EventPreviewForm} />} />

                <Route path="/create-event" element={<PrivateRoute element={EventCreationForm} />} />
                <Route path="/auth/callback/apple" element={<AppleCallback />} />
                <Route path="/compliance/sms" element={<CompliancePage />} />
                <Route path="/compare/:slug" element={<ComparePage />} />

                <Route
                  path="/create-event/step/:step"
                  element={
                    <PrivateRoute element={(props) => <EventCreationForm {...props} editMode={false} />} />
                  }
                />

                <Route path="/faq" element={<FAQ />} />
                <Route path="/subpage" element={<Subpage />} />
                <Route path="/events/:uniqueUrl" element={<EventPage />} />
                <Route path="/qrcode-templates" element={<QRCodeTemplates />} />
                <Route path="/docs" element={<Docs />} />
                <Route path="/terms" element={<TermsAndConditions />} />
                <Route path="/privacy-policy" element={<PrivatePolicy />} />
                <Route path="/help" element={<HelpSupport />} />

                {/* ✅ FIX: entry route that works for BOTH docId and uniqueUrl slug */}
                <Route
                  path="/edit-event/:eventKey"
                  element={<PrivateRoute element={EditEventEntryResolver} />}
                />

                {/* ✅ IMPORTANT: Redirect step 3 (QR) to preview so it never enters edit flow */}
                <Route
                  path="/edit-event/:eventId/step/3"
                  element={<PrivateRoute element={EditQrRedirect} />}
                />

                {/* ✅ FIX: real step route with :step param (EventCreationForm reads this) */}
                <Route
                  path="/edit-event/:eventId/step/:step"
                  element={
                    <PrivateRoute element={(props) => <EventCreationForm {...props} editMode={true} />} />
                  }
                />

                {/* ✅ FIX: alias routes so old /step1 /step2 URLs DO NOT reset steps */}
                <Route
                  path="/edit-event/:eventId/step1"
                  element={<PrivateRoute element={() => <EditStepAliasRedirect stepNumber={1} />} />}
                />
                <Route
                  path="/edit-event/:eventId/step2"
                  element={<PrivateRoute element={() => <EditStepAliasRedirect stepNumber={2} />} />}
                />

                {/* ✅ Redirect old step3 alias straight to preview */}
                <Route
                  path="/edit-event/:eventId/step3"
                  element={<PrivateRoute element={EditQrRedirect} />}
                />

                <Route path="/post-verify" element={<PostVerify />} />
                <Route path="/__auth/action" element={<EmailActionHandler />} />
                <Route path="/storybook-faq" element={<StorybookFAQ />} />
                <Route path="/about" element={<About />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/blog/:slug/:variant" element={<BlogPost />} />
                <Route path="/elevate-event-reviews" element={<ElevateEventReviews />} />
              </Routes>

              {state?.backgroundLocation && (
                <Routes>
                  <Route path="/subpage" element={<Subpage />} />
                </Routes>
              )}
            </Suspense>
          </Box>
        </Box>
      </Box>
    </div>
  );
};

export default App;